import { AutoAwesomeOutlined } from '@mui/icons-material';
import {
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import _ from 'lodash';
import { useLoadingOverlay } from 'modules/components/layer00';
import { LoadingMessage } from 'modules/components/layer01';
import { ChatBubble } from 'modules/components/layer02';
import { CalculationMetricTableRow } from 'modules/components/layer22';
import {
  useMemo,
  useRef
} from 'react';
import { useParams } from 'react-router-dom';
import * as ds from './dataService';



export type CalculationMetricTableProps = {
  showChatBubble?: boolean;
  calculations?: string[];

  disableCalculations?: string[];
};

export const FC = ({
  calculations, showChatBubble, disableCalculations
}: CalculationMetricTableProps) => {
  const slideRef = useRef<HTMLDivElement>(null);
  const setOfCalculations = useMemo(
    () => new Set(calculations ?? []),
    [calculations]
  );

  const params = useParams();
  const { data, loading } = ds.useQuery(params.companyId ?? '');
  useLoadingOverlay({
    loading
  });

  return (
    <Stack
      sx={{
        py: 2
      }}
      gap={2}
    >
      {showChatBubble && (loading
        ? <LoadingMessage.FC />
        : (
          <ChatBubble.FC
            key={params.companyId}
            slideRef={slideRef}
            variant='info'
            role='SHERLOCK'
            text={(
              <span>
                👋 I&apos;ve created over {data.calculations.length} sentiment categories to aid in your company analysis. When you see <AutoAwesomeOutlined />, hover over it to gain my AI insights 🧠.
              </span>
            )}
          />
        )
      )}
      <div ref={slideRef}>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell
                  variant='head'
                >
                  Label
                </TableCell>

                <TableCell
                  variant='head'
                >
                  Inputs
                </TableCell>

                <TableCell
                  variant='head'
                >
                  Output
                </TableCell>

                <TableCell
                  variant='head'
                >
                  Sentiment
                </TableCell>

                <TableCell
                  variant='head'
                >
                  Rules
                </TableCell>
              </TableRow>
            </TableHead>

            {loading ? (
              <TableBody>
                {_.times(calculations?.length ?? 10).map((r) => (
                  <TableRow key={r}>
                    <TableCell>
                      <Skeleton sx={{
                        minWidth: 100
                      }}
                      />
                    </TableCell>

                    <TableCell>
                      <Skeleton sx={{
                        minWidth: 100
                      }}
                      />
                    </TableCell>

                    <TableCell>
                      <Skeleton sx={{
                        minWidth: 100
                      }}
                      />
                    </TableCell>

                    <TableCell>
                      <Skeleton sx={{
                        minWidth: 100
                      }}
                      />
                    </TableCell>

                    <TableCell>
                      <Skeleton sx={{
                        minWidth: 100
                      }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                {data.calculations
                  .filter((calc) => setOfCalculations.size === 0 || setOfCalculations.has(calc.metric.name))
                  .map((calc, i) => (
                    <CalculationMetricTableRow.FC
                      overlayText={(calc.sentiment !== 'nmf' && disableCalculations?.includes(calc.metric.name)) ? 'This calculation is out of date.' : undefined}
                      key={`${calc.id}-${i}`}
                      calculation={calc}
                    />
                  ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </div>
    </Stack>
  );
};
